"use client"

import { cn } from "@/lib/utils"
import { AudioLinesIcon } from "@/components/ui/audio-lines"

interface LogoProps {
  className?: string
  iconClassName?: string
  textClassName?: string
  showIcon?: boolean
  showText?: boolean
  iconPosition?: "left" | "right"
  /** Icon size variant - affects the height of the icon */
  size?: "sm" | "md" | "lg"
}

/**
 * Tesla Lock Sound logo.
 *
 * OWNER OVERRIDE, 2026-08-15: this is the PREVIOUS production lockup, the
 * audio-lines glyph beside "Tesla Lock Sound" with "Lock" in the accent. It
 * replaces §18's "Plate" tile and its 0.3em wordmark tracking, which the
 * owner asked to revert while reviewing the redesign. The Plate is now
 * retired everywhere: `lib/brand-mark.ts` draws this same glyph, so the
 * favicons, app icons and email logo generated by
 * `scripts/brand/generate-brand-assets.ts` are the same object as this one.
 *
 * Two things about colour that are easy to get wrong:
 *
 * 1. The glyph inherits the nav's own ink (see BrandMark's comment below);
 *    the "Lock" word reads `var(--tls-brand-ink)`, the accent-as-TEXT role
 *    token (PRODUCT.md's Colour rule — raw `--tls-brand` is a FILL value
 *    and fails WCAG 1.4.3/1.4.11 as text: dark theme measures 3.12:1 on the
 *    header canvas, 2.49:1 on the footer's canvas-soft, both below the
 *    4.5:1/3:1 bar). --tls-brand-ink is itself band-aware inside the header:
 *    when the nav sits transparently over an inverted panel, app/globals.css
 *    re-points it (alongside --tls-brand) to whichever of --tls-brand /
 *    --tls-signal-on-dark is legible against THAT band, which is the
 *    opposite pairing in each theme (dark theme's band is paper, light
 *    theme's is near-black). Do not hardcode a hex here.
 *
 * 2. The wordmark collapses below `md` (768px) — mark-only, per §18.3's rule
 *    for containers under the lockup floor. The name stays in the
 *    accessibility tree via `sr-only`, so it is removed from the picture and
 *    never from the page.
  */

// Icon sizes in pixels for each variant. All three have hand-fitted drawings.
const iconSizes = {
  sm: 20,   // for navbar
  md: 24,   // for footer/medium contexts
  lg: 32,   // for large displays
} as const

/**
 * OWNER OVERRIDE, 2026-08-15: the mark is the previous production logo, the
 * audio-lines glyph, not §18's "Plate" tile. The owner asked for the old logo
 * back while reviewing the redesign, and `lib/brand-mark.ts` was rewritten to
 * draw this glyph so every static asset matches this component.
 *
 * This is the animated, stroke-based drawing; `lib/brand-mark.ts` is the same
 * geometry as a fill, for rasterisers. The two are documented against each
 * other — change one and check the other.
 *
 * Colour: the glyph inherits `currentColor` from the lockup, which is set to
 * the nav's own ink. That keeps it correct over an inverted panel, where
 * --tls-nav-ink flips to canvas, without a second drawing.
 */
function BrandMark({ size, className }: { size: number; className?: string }) {
  return (
    <AudioLinesIcon
      size={size}
      className={cn("flex-shrink-0", className)}
      aria-hidden="true"
    />
  )
}

export function Logo({
  className,
  iconClassName,
  textClassName,
  showIcon = true,
  showText = true,
  // §18.3's horizontal lockup reads mark-then-wordmark.
  iconPosition = "left",
  size = "sm",
}: LogoProps) {
  const icon = showIcon && (
    <BrandMark size={iconSizes[size]} className={iconClassName} />
  )

  const text = showText && (
    <span
      className={cn(
        "font-display font-bold whitespace-nowrap tracking-tight",
        // Mark-only below md; full horizontal lockup at and above it.
        "sr-only md:not-sr-only md:inline",
        textClassName
      )}
      // OWNER OVERRIDE 2026-08-15: the old wordmark, which is tight-tracked
      // with "Lock" in the accent. §18.9's 0.3em is a property of the Plate
      // lockup that was replaced above, so it goes with it.
    >
      Tesla <span style={{ color: "var(--tls-brand-ink)" }}>Lock</span> Sound
    </span>
  )

  return (
    <span
      className={cn(
        "inline-flex min-w-0 max-w-full items-center gap-2",
        className
      )}
    >
      {iconPosition === "left" && icon}
      {text}
      {iconPosition === "right" && icon}
    </span>
  )
}

/**
 * Standalone mark, for favicons, small spaces, and anywhere the wordmark would
 * not fit. §18.3: mark only, for any container under the lockup's floor.
 */
export function LogoIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return <BrandMark size={size} className={className} />
}
